import { FC, useRef, useState } from 'react';
import { Modal } from '../components/Modal';
import { IonContent, isPlatform } from '@ionic/react';
import { Divider } from '../components/Divider';
import { TextField } from '../components/TextField';
import { Text } from '../components/Text';
import { ShareLinkButton } from '../share-caremap/ShareLinkButton';
import { createUseStyles } from 'react-jss';
import { CareMapState } from '../caremap/CareMapState';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { MixPanelEventEmitter as MixpanelService } from '../metrics/MixPanelEventEmitter';
import { CommunityModelFactory } from '../share-caremap/CommunityModelFactory';
import { ICaremapAccess } from '../caremap/ICareMap';
import { useSubscription } from '@meraki-internal/state';

const useStyles = createUseStyles({
    upperSection: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',

        '& > ion-text': {
            paddingTop: 20,
            paddingBottom: 20,
            maxWidth: 315,
            textAlign: 'center'
        }
    },

    lowerSection: {
        padding: 20
    },

    textFieldWrapper: {
        paddingTop: 20,
        paddingBottom: 20
    },

    sendButtonWrapper: {
        paddingTop: 12
    }
});

export type ICommunityInviteModalProps = {
    isOpen: boolean;
    access: ICaremapAccess;
    closeHandler: () => void;
};

export const CommunityInviteModal: FC<ICommunityInviteModalProps> = ({
    isOpen = false,
    closeHandler,
    access
}) => {
    const classes = useStyles();

    const careMapState = useInstance(CareMapState);

    const factory = useInstance(CommunityModelFactory);
    useSubscription(() => factory);
    const community = factory.forActiveCaremap();

    const mixpanel = useInstance(MixpanelService);

    const [inviteeName, setInviteeName] = useState<string>('');
    const [inviteeEmail, setInviteeEmail] = useState<string>('');

    const fieldRef = useRef<HTMLIonInputElement>(null);
    const focusInvitee = () => {
        const timer = setTimeout(() => {
            if ((isPlatform('desktop') || isPlatform('capacitor')) && fieldRef.current) {
                fieldRef.current.setFocus();
                clearTimeout(timer);
            }
        }, 10);
    };

    const didPresentHandler = () => {
        focusInvitee();
        if (access === 'co-owner'){
            mixpanel.track('Invite Co-Owner Modal Opened');
        }
        else {
            mixpanel.track('Invite Permanent Viewer Modal Opened');
        }

    };

    const onLinkCopied = () => {
        const eventName = access === 'co-owner' ? 'Co-Owner Share Link Copied' : 'Permanent Viewer Share Link Copied';

        mixpanel.track(eventName, () => ({
            'Includes Email': Boolean(inviteeEmail)
        }));
    };

    const onMobileSharingOpened = () => {
        if (access === 'co-owner'){
            mixpanel.track('Co-Owner Mobile Sharing Opened');
        }
        else {
            mixpanel.track('Permanent Viewer Mobile Sharing Modal Opened');
        }

    };

    const getInviteLink = async () => {
        const url = await careMapState.getInviteLink({ inviteeName, inviteeEmail, access, patientName: careMapState.getPatientFirstName() });
        await community.load();
        return url;
    };

    const isValid = Boolean(inviteeName);

    const label = access === 'view-only' ? 'Viewer' : 'Co-Owner';
    const description = access === 'view-only' ?
        `Add viewers to ${careMapState.getPatientFirstName()}'s community to keep them updated and get support` :
        `Share ${careMapState.getPatientFirstName()}'s innerhive and get the help you need coordinating care`;

    const shareDescription = access === 'view-only' ?
        `You’ve been added to ${careMapState.getPatientFirstName()}’s Innerhive! You’ll now receive email updates and you can join their community here.` :
        `Join our hive and become a co-owner of ${careMapState.getPatientFirstName()}'s innerhive with me. Open the link to get started!`;

    const clearForm = ()=> {
        setInviteeName('');
        setInviteeEmail('');
    };

    return (
        <Modal
            stackedModal
            isOpen={isOpen}
            data-id="invite-co-owner-modal"
            title={access === 'view-only' ? 'Add Viewer' : 'Invite Co-Owner'}
            closeHandler={closeHandler}
            onWillPresent={() => clearForm()}
            onDidPresent={didPresentHandler}
        >
            <IonContent>
                <div className={classes.upperSection}>
                    <img
                        style={{width: '30%', maxWidth: access === 'view-only' ? 100 : 115}}
                        src="./assets/images/share/invite-co-owner.png" alt="invite-img"
                    />
                    <Text>{description}</Text>
                </div>
                <Divider />
                <div className={classes.lowerSection}>
                    <div className={classes.textFieldWrapper}>
                        <TextField
                            //autoFocus
                            blurOnEnter
                            inputRef={fieldRef}
                            name="name"
                            value={inviteeName}
                            autocapitalize="words"
                            label={`${label}'s Name`}
                            onInput={(value) => setInviteeName(value)}
                        />
                    </div>
                    <div className={classes.textFieldWrapper}>
                        <TextField
                            blurOnEnter
                            name="email"
                            value={inviteeEmail}
                            autocapitalize="words"
                            label={`${label}'s Email`}
                            onInput={(value) => setInviteeEmail(value)}
                        />
                    </div>
                    <Text type="subtitle">
                        <span style={{fontWeight: 'normal'}}>
                            {access === 'view-only' ? (
                                <span>A <strong>viewer</strong> will automatically receive updates at this email address, and have view-access to {careMapState.getPatientFirstName()}'s innerhive. They don't need to create an account right away, but if they want to you can send them the link below.</span>
                            ): (
                                <span>A <strong>Co-Owner</strong> has full access to {careMapState.getPatientFirstName()}'s innerhive (including anything marked private) and can invite co-owner's.</span>
                            )}
                        </span>
                    </Text>
                    <div className={classes.sendButtonWrapper}>
                        <ShareLinkButton
                            disabled={!isValid}
                            data-type="send-co-owner-invite-button"
                            buttonLabel={access === 'view-only' ? 'Add Viewer' : 'Invite Co-Owner'}
                            buttonLabelWhenNativeShare={access === 'view-only' ? `Add Viewer` : `Invite Co-Owner`}
                            shareSubject={access === 'view-only' ? `You've been added to ${careMapState.getPatientFirstName()}’s innerhive!`: `Become a co-owner of ${careMapState.getPatientFirstName()}’s innerhive!`}
                            shareBody={shareDescription}
                            createLinkUrl={getInviteLink}
                            onLinkCopied={onLinkCopied}
                            onMobileSharingOpened={onMobileSharingOpened}
                            onMobileSharingDone={closeHandler}
                        />
                    </div>
                </div>
            </IonContent>
        </Modal>
    );
};
